import { alertUrl } from './alert-url.js';
import { youtubeIds } from './newsroom.js';
import { clean, cleanBio, orgOf } from './profile-seo.js';
import { SITE_NAME } from './seo-title.js';
import truncate from './text-truncate.js';

// Enough to credit every quoted expert on a busy alert without the JSON-LD
// outweighing the responses it describes.
const MAX_AUTHORS = 50;

// Same length as the page's meta description, which reads the same body.
const DESCRIPTION_MAX = 160;

// An alert is tagged with every expertise it was sent to, eighty on a busy one.
// Enough keywords to cover the topic, not the whole directory.
const MAX_KEYWORDS = 50;

const isoDateTime = (value) => {
    // `new Date(null)` is the epoch, not an invalid date — guard falsy first
    const date = value ? new Date(value) : null;
    return !date || Number.isNaN(date.getTime())
        ? undefined
        : date.toISOString();
};

const prune = (node) =>
    Object.fromEntries(
        Object.entries(node).filter(
            ([, value]) =>
                value !== undefined &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0)
        )
    );

const publisher = (host) => ({
    '@type': 'Organization',
    name: SITE_NAME,
    url: host,
    logo: { '@type': 'ImageObject', url: `${host}/newspage.png` },
});

// The firm's social profiles, as the profile's socialNetwork row holds them:
// one column per network, empty when not set. Only absolute http(s) links are
// worth a sameAs; anything else (a handle, a stray word) would mislead.
const socialUrls = (socialNetwork) =>
    Object.entries(socialNetwork || {})
        .map(([, value]) => clean(value))
        .filter((value) => /^https?:\/\//i.test(value));

const unique = (values) => [...new Set(values.filter(Boolean))];

// Profiles type their website as they like: "www.example.com" is common. A
// sameAs or Organization url has to be absolute, so add the scheme or leave
// the value out.
export const webUrl = (value) => {
    const text = clean(value);
    if (/^https?:\/\//i.test(text)) {
        return text;
    }
    return /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(text)
        ? `https://${text}`
        : undefined;
};

// The firm an expert speaks for, as the response card words it: "{position} at
// {employer || profile name}" (Response.js). An Organization only when there is
// a name to give it.
const firmOf = (profile, logo) => {
    const name = orgOf(profile) || clean(profile.name);
    return name
        ? prune({
              '@type': 'Organization',
              name,
              url: webUrl(profile.website),
              logo,
          })
        : undefined;
};

// Each response is quoted under the expert's name when one was picked, and the
// company's otherwise — the same name the alert page shows. Both link to the
// company profile, which is the only public page either has. Reads name, role,
// firm, image and social links only: the response payload carries contact
// details the schema must not.
export function expertPersons(responses, host) {
    const seen = new Set();

    return (Array.isArray(responses) ? responses : [])
        .map(({ expert, profile } = {}) => {
            const name = clean(expert?.name) || clean(profile?.name);
            if (!name) {
                return null;
            }
            // a deactivated responder's page answers 404: named, but not linked
            const linked = !profile?.deactivated;
            const slug = linked ? clean(profile?.slug) : '';
            const url = slug ? `${host}/${slug}` : undefined;
            const logo = clean(profile?.logo?.path) || undefined;
            return prune({
                '@type': 'Person',
                name,
                url,
                jobTitle: clean(expert?.position) || clean(profile?.position),
                // the response card's picture: the expert's own, else the logo
                image: clean(expert?.image?.path) || logo,
                worksFor: profile ? firmOf(profile, logo) : undefined,
                sameAs: linked
                    ? unique([url, ...socialUrls(profile?.socialNetwork)])
                    : undefined,
            });
        })
        .filter((person) => {
            const key = person && `${person.name}|${person.url}`;
            if (!person || seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        })
        .slice(0, MAX_AUTHORS);
}

// Editors caption a video with an italic line straight after it.
const videoCaption = (body, id) => {
    const match = new RegExp(
        `embed/${id}[^]*?</figure>\\s*<p>\\s*<(i|em)>([^]*?)</\\1>\\s*</p>`
    ).exec(body || '');
    return match ? cleanBio(match[2]) : '';
};

// A VideoObject per embedded YouTube video, so the story can show as a video
// result. uploadDates are YouTube's own, by video id (utils/youtube-upload-date.js).
// Google requires uploadDate, so without YouTube's the story's last save stands
// in: the video was on the page by then. hqdefault exists for every video,
// maxresdefault not.
export const storyVideos = (
    body,
    uploadDates,
    { headline, description, savedAt }
) =>
    youtubeIds(body).map((id) =>
        prune({
            '@type': 'VideoObject',
            name: clean(headline),
            description: videoCaption(body, id) || clean(description),
            thumbnailUrl: [`https://i.ytimg.com/vi/${id}/hqdefault.jpg`],
            uploadDate: isoDateTime(uploadDates?.[id]) || isoDateTime(savedAt),
            embedUrl: `https://www.youtube.com/embed/${id}`,
            url: `https://www.youtube.com/watch?v=${id}`,
        })
    );

export const authorPath = (slug) =>
    slug ? `/authors/${encodeURIComponent(slug)}` : null;

// A story's byline as schema.org reads it: the picked author with their page,
// photo, title and LinkedIn (the Newsdesk as an Organization), a typed byline
// as a bare Person, and null when the story has neither.
export function bylineAuthor(story, host) {
    const author = story?.bylineAuthor;
    const name = clean(author?.name) || clean(story?.byline);
    if (!name) {
        return null;
    }
    if (!author) {
        return { '@type': 'Person', name };
    }
    return prune({
        '@type': author.isOrganization ? 'Organization' : 'Person',
        name,
        url: `${host}${authorPath(author.slug)}`,
        image: webUrl(author.photoUrl),
        jobTitle: author.isOrganization ? undefined : clean(author.jobTitle),
        sameAs: unique([webUrl(author.linkedinUrl)]),
    });
}

// An author's page: Google reads ProfilePage as the page about that person.
export const authorPageSchema = (author, host) => ({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: bylineAuthor({ bylineAuthor: author }, host),
});

// The alert's tags (expertise) by level: the top of the tree is the sector the
// alert sits in, the master tags are its named topics, and the long tail of
// child tags is only worth a keyword.
const names = (tags) => unique(tags.map((tag) => clean(tag?.name)));

const topicsOf = (alert) => {
    const tags = Array.isArray(alert?.expertise) ? alert.expertise : [];
    const sections = names(tags.filter((tag) => !tag?.parentId));
    const masters = names(tags.filter((tag) => tag?.isMaster));
    return {
        sections,
        topics: masters.length ? masters : sections,
        keywords: names([
            ...tags.filter((tag) => tag?.isMaster),
            ...tags,
        ]).slice(0, MAX_KEYWORDS),
    };
};

export default function buildNewsArticleSchema(alert, responses, host) {
    const headline = clean(alert?.title);
    const slug = clean(alert?.slug);
    if (!headline || !slug) {
        return null;
    }

    const url = alertUrl(slug, host);
    // publicationDate is null on every alert today, see data/sitemapNews.mjs
    const published = isoDateTime(alert.publicationDate || alert.createdAt);
    const updated = isoDateTime(alert.updatedAt);
    const people = expertPersons(responses, host);
    const { sections, topics, keywords } = topicsOf(alert);
    // tags gone and entities decoded: JSON-LD text is not HTML
    const body = cleanBio(alert.body);

    return prune({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline,
        description: body ? truncate(body, DESCRIPTION_MAX, true) : undefined,
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        image: [`${host}/newspage.png`],
        datePublished: published,
        // an alert never edited has no updatedAt, and one edited before its
        // publicationDate would otherwise predate itself
        dateModified:
            updated && published && updated > published ? updated : published,
        author: people.length ? people : [publisher(host)],
        publisher: publisher(host),
        // the alert's sector, its main topics, and every tag as a keyword
        articleSection: sections,
        about: topics.map((name) => ({ '@type': 'Thing', name })),
        keywords,
        isAccessibleForFree: true,
    });
}
